/* =========================================================
   MAISON FRONTEND — PERFORMANCE OPTIMISED DROP-IN
   - scoped MutationObserver (grid + predictive search, subtree)
   - src-keyed hydration (survives Horizon node morphing/reuse)
   - no per-scroll layout reads (cached + re-measured on change)
   - batched DOM updates
   - safe lazy init
========================================================= */

(function () {
  "use strict";

  /* =========================
     UTIL: RAF THROTTLE
  ========================== */
  function rafThrottle(fn) {
    let ticking = false;
    return function (...args) {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        ticking = false;
        fn.apply(this, args);
      });
    };
  }

  /* =========================
     1. MAISON GALLERY
  ========================== */
  function initMaisonGallery(root = document) {
    const galleries = root.querySelectorAll?.('.maison-card-gallery') || [];

    galleries.forEach(gallery => {
      if (gallery.__maisonGalleryInit) return;

      const slideshow = gallery.querySelector('slideshow-component');
      if (slideshow?.getAttribute('disabled') === 'true') return;

      const scroller = gallery.querySelector('[ref="scroller"]');
      const bar = gallery.querySelector('.maison-mobile-progress__bar');

      if (!scroller || !bar) return;

      gallery.__maisonGalleryInit = true;

      // fixed because always 2 images
      bar.style.width = '50%';

      // cached: invariant during scroll, recomputed on resize only
      let maxScroll = scroller.scrollWidth - scroller.clientWidth;

      const update = () => {
        const progress = maxScroll > 0
          ? scroller.scrollLeft / maxScroll
          : 0;

        bar.style.transform = `translateX(${progress * 100}%)`;
      };

      scroller.addEventListener('scroll', rafThrottle(update), { passive: true });

      window.addEventListener('resize', rafThrottle(() => {
        maxScroll = scroller.scrollWidth - scroller.clientWidth;
        update();
      }));

      update();
    });
  }

  /* =========================
     2. PRODUCT GRID WATCHER (SCOPED + SUBTREE)
     subtree:true is required so infinite-scroll injections into the
     inner <ul> (not direct children of the container) are caught.
  ========================== */
  function watchProductGrid() {
    const grid = document.querySelector('.product-grid-container');
    if (!grid) return;

    let scheduled = false;

    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;

      requestAnimationFrame(() => {
        scheduled = false;
        initMaisonGallery(grid);
        hydrateProductImages(grid);
      });
    });

    // childList only -> class mutations don't retrigger; node add/remove does.
    // (src-attribute morphing isn't observed here; add attributeFilter below
    // too if this grid ever starts morphing cards instead of replacing them.)
    observer.observe(grid, { childList: true, subtree: true });
  }

  /* =========================
     2b. PREDICTIVE SEARCH WATCHER (SCOPED + SUBTREE + ATTRS)
     Horizon's predictive-search-component MORPHS existing DOM nodes
     rather than replacing them wholesale. The meaningful image change
     on a morph lands on srcset/currentSrc (responsive + SpeedSize), NOT
     necessarily on src — so a src-only attributeFilter never woke for
     typed-search renders (that was the "works on 2nd search, not 1st"
     and flickering-dark symptom). We observe attributes broadly here.

     Cost note: this subtree is ONLY the search dropdown — a handful of
     cards, live only while search is open — so unfiltered attribute
     observing is effectively free. The grid observer above stays
     filtered/narrow because that subtree is large and long-lived.

     The dropdown also shows "recently viewed" content BEFORE any search
     is performed, so it needs an immediate hydration pass at boot too —
     not just a reaction to later mutations.
  ========================== */
  function watchPredictiveSearch() {
    const searchRoot = document.querySelector('predictive-search-component');
    if (!searchRoot || searchRoot.__maisonSearchObserverAttached) return;

    searchRoot.__maisonSearchObserverAttached = true;

    // hydrate whatever's already rendered (e.g. recently-viewed) at boot
    hydrateProductImages(searchRoot);
    initMaisonGallery(searchRoot);

    let scheduled = false;

    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;

      requestAnimationFrame(() => {
        scheduled = false;
        hydrateProductImages(searchRoot);
        initMaisonGallery(searchRoot); // no-op if search cards don't use the gallery
      });
    });

    // Unfiltered attributes on purpose (see block comment). No loop risk:
    // our own is-loaded writes only fire for a genuinely new src, and any
    // re-fire on the same src early-returns via __hydratedSrc before
    // touching a class.
    observer.observe(searchRoot, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  }

  /* =========================
     2c. PREDICTIVE SEARCH OPEN TRIGGER (DELEGATED)
     The dialog is opened by <search-button>'s button, which lives in the
     header, outside predictive-search-component. Recently-viewed content
     may only become visible/hydratable once the dialog actually opens
     (e.g. lazy images that stayed display:none don't start loading until
     then), so re-run hydration right after the open click. Delegated on
     document so it survives header re-renders — same pattern as
     initShowMore below.
  ========================== */
  function initPredictiveSearchOpenTrigger() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('search-button');
      if (!trigger) return;

      const searchRoot = document.querySelector('predictive-search-component');
      if (!searchRoot) return;

      // rAF, not setTimeout: waits one paint for the dialog's own open
      // logic to run first, still cheap and non-blocking.
      requestAnimationFrame(() => {
        hydrateProductImages(searchRoot);
        initMaisonGallery(searchRoot);
      });
    });
  }

  /* =========================
     3. IMAGE HYDRATION (SCOPED, SRC-KEYED)
     Keyed on the actual src rather than "seen this node before", because
     morphing frameworks (Horizon) reuse <img> nodes across re-renders —
     a node-level __hydrated flag would permanently block re-processing
     once a reused node's src changes to a different product's image.
  ========================== */
  function hydrateProductImages(root = document) {
    const medias = root.querySelectorAll('.product-media');

    medias.forEach(media => {
      const img = media.querySelector('.product-media__image');
      if (!img) return;

      const src = img.currentSrc || img.src;
      if (!src || img.__hydratedSrc === src) return; // this exact image already handled

      img.__hydratedSrc = src;

      // node may have been morphed/reused for a different product — reset visual state
      media.classList.remove('is-loaded');
      img.classList.remove('is-loaded');

      const markLoaded = () => {
        // Only care that *an* image is decoded and painted here — NOT that it's
        // byte-for-byte the src we captured above. Responsive resolution
        // (srcset + SpeedSize) advances currentSrc past the captured value, so
        // the old strict `currentSrc !== src` guard rejected perfectly valid
        // loads — that was the real cause of first-search / recently-viewed
        // failing to light up. naturalWidth > 0 is enough to confirm a decode.
        if (!(img.complete && img.naturalWidth > 0)) return;
        media.classList.add('is-loaded');
        img.classList.add('is-loaded');
      };

      if (img.complete && img.naturalWidth > 0) {
        markLoaded();
      } else {
        img.addEventListener('load', markLoaded, { once: true });
      }
    });
  }

  /* =========================
     4. SLIDER FADE (NO PER-SCROLL LAYOUT READ)
     Single slider container; cross-slide correctness comes from
     re-measuring footerTop on slideshow:select, NOT from iterating.
  ========================== */
  function initSliderFade() {
    const slider = document.querySelector('.maison-home-slider');
    const footer = document.querySelector('footer');
    if (!slider || !footer) return;

    let footerTop = footer.offsetTop;

    const measure = () => {
      footerTop = footer.offsetTop;
    };

    const onScroll = rafThrottle(() => {
      const scrollBottom = window.scrollY + window.innerHeight;
      const isVisible = scrollBottom > footerTop - 100;
      slider.classList.toggle('is-faded', isVisible);
    });

    // slides have different heights -> document height changes per slide,
    // so footerTop must be refreshed when the active slide swaps.
    const onSlideChange = () => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      requestAnimationFrame(() => {
        measure();
        onScroll();
      });
    };

    document.addEventListener('slideshow:select', onSlideChange);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);

    measure();
    onScroll();
  }

  /* =========================
     5. VARIANT PICKER (MINIMAL DOM WORK)
  ========================== */
  function initVariantPicker() {
    document.querySelectorAll('variant-picker').forEach(picker => {
      picker.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;

        const variantId = input.dataset.variantId;
        const matched = window.maisonVariants?.find(
          v => String(v.id) === String(variantId)
        );
        if (!matched) return;

        document.querySelectorAll('[data-variant-meta-wrapper]').forEach(wrapper => {
          const priceEl = wrapper.querySelector('[data-variant-price]');
          if (priceEl) priceEl.textContent = matched.price;

          const existing = wrapper.querySelector('[data-variant-production-time]');

          if (!matched.production_time) {
            existing?.remove();
            return;
          }

          if (existing) {
            existing.querySelector('span').textContent = matched.production_time;
            existing.classList.add('is-visible');
          } else {
            priceEl?.insertAdjacentHTML(
              'afterend',
              `<div class="maison-variant-production-time is-visible" data-variant-production-time>
                <span>${matched.production_time}</span>
              </div>`
            );
          }
        });
      });
    });
  }

  /* =========================
     6. COOKIE PREFS
  ========================== */
  function initCookiePreferences() {
    document.querySelectorAll('a[href*="#cookie-preferences"]').forEach(link => {
      link.addEventListener('click', function (e) {
        e.preventDefault();

        if (typeof privacyBanner !== 'undefined' && privacyBanner.showPreferences) {
          privacyBanner.showPreferences();
        }
      });
    });

    if (window.location.hash === '#cookie-preferences') {
      if (typeof privacyBanner !== 'undefined' && privacyBanner.showPreferences) {
        privacyBanner.showPreferences();
      }
    }
  }

  /* =========================
     7. SHOW MORE (DELEGATED)
  ========================== */
  function initShowMore() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.maison-show-more__button');
      if (!button) return;

      const wrapper = button.closest('.maison-show-more');
      if (!wrapper) return;

      const moreLabel = wrapper.querySelector('.maison-show-more__label--more');
      const lessLabel = wrapper.querySelector('.maison-show-more__label--less');

      const expanded = wrapper.classList.toggle('is-expanded');

      if (moreLabel) moreLabel.style.display = expanded ? 'none' : '';
      if (lessLabel) lessLabel.style.display = expanded ? '' : 'none';
    });
  }

  /* =========================
     BOOTSTRAP
  ========================== */
  function boot() {
    const hasProductGrid = document.querySelector('.product-grid-container');
    const hasGallery = document.querySelector('.maison-card-gallery');
    const hasPDP = document.querySelector('variant-picker');
    const hasHomeSlider = document.querySelector('.maison-home-slider');

    /* always safe (delegated / globally scoped) */
    initCookiePreferences();
    initShowMore();

    /* predictive search lives in the header on every page */
    watchPredictiveSearch();
    initPredictiveSearchOpenTrigger();

    /* product-related pages only */
    if (hasGallery || hasProductGrid) {
      initMaisonGallery();
      watchProductGrid();
      hydrateProductImages();
    }

    /* PDP only */
    if (hasPDP) {
      initVariantPicker();
    }

    /* homepage only */
    if (hasHomeSlider) {
      initSliderFade();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

})();